import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    ConfirmPhoneVerificationMethod,
    type ConfirmPhoneVerificationRequest,
    type ConfirmPhoneVerificationResponseSuccess,
    type ConfirmPhoneVerificationSearchParams,
    getConfirmPhoneVerificationUrl,
    getStartPhoneVerificationUrl,
    type SchemaConfirmPhoneVerificationProblemType,
    type SchemaStartPhoneVerificationProblemType,
    StartPhoneVerificationMethod,
    type StartPhoneVerificationResponseSuccess,
    type StartPhoneVerificationSearchParams,
} from '@/shared/api/openApi';
import { request } from '@/shared/api/request';
import { getRequestContext } from '@/shared/lib/requestContext';
import { isErrorWithType } from '@/shared/lib/requests';

export const phoneVerificationApi = createApi({
    name: 'entities/order/api/phoneVerificationApi',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            // phone не нужен для самого запроса, но используется для автоматического рефетча при изменении номера телефона
            startPhoneVerification: builder.query<StartPhoneVerificationResponseSuccess, { phone: string }>({
                query: () => {
                    const { signature, storeSlug, orderId } = getRequestContext();

                    return {
                        method: StartPhoneVerificationMethod,
                        url: getStartPhoneVerificationUrl({ order_id: orderId, store_slug: storeSlug }),
                        params: { signature } satisfies StartPhoneVerificationSearchParams,
                    };
                },
            }),
            confirmPhoneVerification: builder.mutation<
                ConfirmPhoneVerificationResponseSuccess,
                ConfirmPhoneVerificationRequest
            >({
                query: (body) => {
                    const { orderId, signature, storeSlug } = getRequestContext();

                    return {
                        method: ConfirmPhoneVerificationMethod,
                        url: getConfirmPhoneVerificationUrl({ order_id: orderId, store_slug: storeSlug }),
                        params: { signature } satisfies ConfirmPhoneVerificationSearchParams,
                        body,
                    };
                },
            }),
        };
    },
});

export const useStartPhoneVerification = phoneVerificationApi.useStartPhoneVerificationQuery;

export const parseStartVerificationError = (error: unknown) => {
    if (isErrorWithType<SchemaStartPhoneVerificationProblemType>(error)) {
        switch (error.responseData.type) {
            case 'ALREADY_VERIFIED':
                return 'Номер уже верифицирован.';
            case 'INVALID_PHONE':
                return 'Неверное написание номера. Проверьте введённый номер.';
            case 'PHONE_BLOCKED':
                return 'Номер заблокирован.';
            case 'PROVIDER_UNAVAILABLE':
                return 'Ошибка провайдера. Пожалуйста, повторите попытку позже.';
            case 'SMS_RATE_LIMITED':
                return 'Слишком много попыток. Пожалуйста, попробуйте позже.';
        }
    }

    return 'Ошибка провайдера. Пожалуйста, повторите попытку позже.';
};

export const useConfirmPhoneVerification = phoneVerificationApi.useConfirmPhoneVerificationMutation;

export const parseConfirmVerificationError = (error: unknown) => {
    if (isErrorWithType<SchemaConfirmPhoneVerificationProblemType>(error)) {
        switch (error.responseData.type) {
            case 'ALREADY_VERIFIED':
                return 'Номер уже верифицирован.';
            case 'INVALID_CODE':
                return 'Неверный код из смс.';
            case 'PROVIDER_UNAVAILABLE':
                return 'Ошибка проверки кода, повторите попытку позже.';
            case 'TOO_MANY_ATTEMPTS':
                return 'Слишком много попыток. Пожалуйста, попробуйте позже.';
        }
    }

    return 'Ошибка проверки кода, повторите попытку позже.';
};
